import { useContext, useEffect, useState } from "react";
import "../Announcement/Announcement.scss";
import { toast } from "react-toastify";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { AppContext } from "../../../context/AppContext";
import api from "../../../api.js";
import { io } from "socket.io-client";
import { Calendar } from "primereact/calendar";
import Loader from "../../../components/Loader/Loader.jsx";

const ContactMessages = () => {
  const { backendUrl } = useContext(AppContext);
  const [allContactMessages, setAllContactMessages] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [socket, setSocket] = useState(null);
  const [scrollHeight, setScrollHeight] = useState("425px");
  let errorShown = false;

  const showConfirmDialog = (rowData) => {
    setSelectedRow(rowData);
    setShowDialog(true);
  };

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerHeight;
      if (screenWidth >= 900) {
        setScrollHeight("650px");
      } else {
        setScrollHeight("440px");
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  async function fetchAllContactMessages() {
    setLoading(true);
    try {
      const { data } = await api.get(`${backendUrl}/api/auth/getAllContacts`);
      setAllContactMessages(data.data);
    } catch (error) {
      if (error.response && error.response.status === 503) {
        toast.error(
          "⚠️ The service is currently under maintenance. Please try again later."
        );
      } else if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        console.error("Unexpected error:", error);
        toast.error("Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    const newSocket = io(backendUrl, {
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      transports: ["websocket"],
    });
    setSocket(newSocket);
    return () => {
      newSocket.disconnect();
    };
  }, [backendUrl]);

  useEffect(() => {
    if (!socket) return;
    fetchAllContactMessages();
    socket.on("update_contact_message", (message) => {
      setAllContactMessages((prev) => {
        if (!prev.some((g) => g.id === message.id)) {
          return [message, ...prev];
        }
        return prev;
      });
      toast.info("📩 New contact message received!");
    });
    return () => {
      socket.off("update_contact_message");
      socket.disconnect();
    };
  }, [socket]);

  const handleDeleteClick = async () => {
    setIsSubmitting(true);
    try {
      const { data } = await api.delete(
        `${backendUrl}/api/auth/deleteContact/${selectedRow.id}`
      );
      if (data.success) {
        toast.success(data.message);
        fetchAllContactMessages();
      }
    } catch (error) {
      if (error.response && error.response.status === 503) {
        toast.error(
          "⚠️ The service is currently under maintenance. Please try again later."
        );
      } else if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        console.error("Unexpected error:", error);
        toast.error("Something went wrong.");
      }
    } finally {
      setShowDialog(false);
      setSelectedRow(null);
      setIsSubmitting(false);
    }
  };

  const formatToDDMMYYYY = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleDownloadContactSubmissions = async () => {
    setIsSubmitting(true);
    if (!startDate || !endDate) {
      toast.warn("Please select both start and end dates.");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error("Start date cannot be after end date.");
      return;
    }
    try {
      const formattedStart = formatToDDMMYYYY(startDate);
      const formattedEnd = formatToDDMMYYYY(endDate);
      const response = await api.get(
        `${backendUrl}/api/auth/exportContactSubmissionsExcel`,
        {
          params: {
            startDate: formattedStart,
            endDate: formattedEnd,
          },
          responseType: "blob",
        }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `Contact_Submissions_${formattedStart}_to_${formattedEnd}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Failed to download Excel file.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="adminAnnoucementMainContainer">
      <Loader visible={isSubmitting} />

      <div className="headerParent">
        <h2>Welcome to Contact Messages Page</h2>
      </div>

      <div className="dateAndDownloadBtnContainer">
        <div className="dateFieldParent">
          <label htmlFor="startDate" className="dateLabelNames">
            Start Date
          </label>
          <Calendar
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.value)}
            dateFormat="dd-mm-yy"
            showIcon
            inputStyle={{ padding: "0.6rem 0.8rem", width: "175px" }}
          />
        </div>
        <div className="dateFieldParent">
          <label htmlFor="endDate" className="dateLabelNames">
            End Date
          </label>
          <Calendar
            id="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.value)}
            dateFormat="dd-mm-yy"
            showIcon
            inputStyle={{ padding: "0.6rem 0.8rem", width: "175px" }}
          />
        </div>
        <div className="downloadExcelBtnParent">
          <Button
            label=" Download Excel"
            icon="pi pi-download"
            className="p-button-success"
            onClick={handleDownloadContactSubmissions}
            disabled={loading}
            style={{
              padding: "0.6rem 1.5rem",
              fontWeight: "600",
              fontSize: "1rem",
              fontFamily: "var(--font-paragraph)",
              gap: "0.5rem",
            }}
          />
        </div>
      </div>

      <ConfirmDialog
        visible={showDialog}
        onHide={() => setShowDialog(false)}
        message={
          <div className="custom-dialog-content">
            <div className="iconParent">
              <i className="pi pi-times"></i>
            </div>
            <div className="contentParent">
              <h2>Are you sure?</h2>
              <p>Message: {selectedRow?.message.substring(0, 100)}...</p>
              <p>Do you really want to delete this message?</p>
              <p>This process cannot be undone.</p>
            </div>
          </div>
        }
        header=""
        icon={null}
        acceptLabel="Delete"
        rejectLabel="Cancel"
        acceptClassName="p-button-danger custom-delete-btn"
        rejectClassName="p-button-secondary custom-cancel-btn"
        accept={handleDeleteClick}
      />

      <article className="tableWrapper">
        <div className="scrollableTableContainer">
          <DataTable
            value={allContactMessages}
            showGridlines
            stripedRows
            className="table"
            emptyMessage="No contact messages available"
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 20]}
            scrollable
            scrollHeight={scrollHeight}
            loading={loading}
          >
            <Column
              header="S.No"
              body={(_, options) => options.rowIndex + 1}
              className="tableData"
              style={{ width: "1%", whiteSpace: "nowrap" }}
              headerStyle={{ whiteSpace: "nowrap", maxWidth: "fit-content" }}
            />
            <Column
              field="name"
              header="Name"
              className="tableData"
              style={{ minWidth: "200px", maxWidth: "325px" }}
            />
            <Column
              field="email"
              header="Email"
              className="tableData"
              style={{ minWidth: "200px", maxWidth: "325px" }}
            />
            <Column
              field="message"
              header="Message"
              className="tableData"
              style={{ minWidth: "275px", maxWidth: "325px" }}
            />
            <Column
              field="createdAt"
              header="Created At"
              body={(rowData) =>
                new Date(rowData.createdAt).toLocaleDateString()
              }
              className="tableData"
              style={{ width: "1%", whiteSpace: "nowrap" }}
              headerStyle={{ whiteSpace: "nowrap", maxWidth: "fit-content" }}
            />
            <Column
              header="Action"
              style={{ width: "1%", whiteSpace: "nowrap" }}
              headerStyle={{ whiteSpace: "nowrap", maxWidth: "fit-content" }}
              body={(rowData) => (
                <div className="action-buttons">
                  <Button
                    label="Delete"
                    severity="danger"
                    className="actionBtns"
                    onClick={() => showConfirmDialog(rowData)}
                  />
                </div>
              )}
              className="tableData"
            />
          </DataTable>
        </div>
      </article>
    </section>
  );
};

export default ContactMessages;
